import { t } from "@lingui/macro";
import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
  StackActions,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { Suspense, useEffect, useState } from "react";
import { StatusBar } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { Box } from "@whoosh/design-system/src/components/primitive";

import { OverlayLoading_atom } from "../../features/OverlayLoader/atoms";
import {
  PatronBookingRateChangeConfirmModal,
  PatronBookingRateChangeConfirmModalRateInitializationData,
} from "../../features/PatronBookingRateChangeConfirmModal/PatronBookingRateChangeConfirmModal";
import { TeeSheet } from "../../features/TeeSheet";
import { useUpdateSlot } from "../../features/TeeSheet/components/TimeSlotList/hooks/useUpdateSlot";
import { ClubStackNavigatorParamList } from "../../navigators/ClubNavigator";
import { MoveBookingScreenQuery } from "./__generated__/MoveBookingScreenQuery.graphql";

export const MoveBookingScreen = () => {
  const [queryRef, loadQuery] = useQueryLoader<MoveBookingScreenQuery>(
    MoveBookingScreenQueryString
  );

  const {
    params: { originalBookingId, date },
  } = useRoute<RouteProp<ClubStackNavigatorParamList, "MoveBooking">>();

  useEffect(() => {
    if (originalBookingId && date) {
      loadQuery({ date, bookingId: originalBookingId });
    }
  }, [loadQuery, originalBookingId, date]);
  return (
    <Suspense>
      {queryRef ? <MoveBookingScreenQueryRenderer queryRef={queryRef} /> : null}
    </Suspense>
  );
};

export const MoveBookingScreenQueryRenderer = ({
  queryRef,
}: {
  queryRef: PreloadedQuery<MoveBookingScreenQuery>;
}) => {
  const data = usePreloadedQuery(MoveBookingScreenQueryString, queryRef);

  const {
    params: { bookingType },
  } = useRoute<RouteProp<ClubStackNavigatorParamList, "MoveBooking">>();

  const agenda = data.node?.startSlot?.agenda;
  const { showToast } = useToast();

  const setLoading = useSetAtom(OverlayLoading_atom);

  const [isRateChangeConfirmModalOpen, setIsRateChangeConfirmModalOpen] =
    useState(false);
  const [rateUpdatesModalData, initializeRateChangeConfirmModal] =
    useState<PatronBookingRateChangeConfirmModalRateInitializationData>();

  const [handleUpdateSlot, isUpdatingSlot] = useUpdateSlot(
    setIsRateChangeConfirmModalOpen,
    initializeRateChangeConfirmModal
  );

  useEffect(() => {
    setLoading(isUpdatingSlot);
  }, [setLoading, isUpdatingSlot]);

  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const bookingId = data.node?.id;

  const moveBookingSuccess = () => {
    showToast({
      description: t`Booking Updated Successfully.`,
      semanticStyle: "success",
      toastId: "moveBookingSuccess",
      testID: "move-booking-toast",
    });
    setLoading(false);

    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.dispatch(
        StackActions.replace("BookingSummary", {
          slotOrBookingId: bookingId,
        })
      );
    }
  };
  const currentMembershipTypeId =
    data.session?.currentClientProfile?.member?.membershipType?.id;

  const handleRowPress = (slotId: string) => {
    if (bookingId && currentMembershipTypeId) {
      setLoading(true);
      handleUpdateSlot(
        {
          bookingId,
          startSlotId: slotId,
          notifyMember: true,
        },
        currentMembershipTypeId,
        bookingType,
        () => {
          moveBookingSuccess();
        }
      );
    }
  };

  const { top, bottom, left, right } = useSafeAreaInsets();

  return (
    <Box
      flex={1}
      bg="surface-primary"
      style={{
        paddingTop: top,
        paddingBottom: bottom,
        paddingLeft: left,
        paddingRight: right,
      }}
    >
      <WhiteBoxToCoverHeaderShadow />
      <StatusBar
        animated={true}
        barStyle="dark-content"
        backgroundColor="white"
      />
      <Box flex={1}>
        {agenda?.date && !!data?.session ? (
          <>
            {/* TODO: Support multislot agendas here */}
            {agenda?.facility?.type === "GOLF_COURSE" ? (
              <TeeSheet
                currentSlotId={data.node?.startSlot?.id || ""}
                memberRef={data?.session}
                handleRowPress={handleRowPress}
                handleRequestedRowPress={() => {}}
                onJoinableRowPress={() => {}}
              />
            ) : null}
          </>
        ) : null}
      </Box>
      <PatronBookingRateChangeConfirmModal
        isOpen={isRateChangeConfirmModalOpen}
        onClose={() => setIsRateChangeConfirmModalOpen(false)}
        rateInitializationData={rateUpdatesModalData}
      />
    </Box>
  );
};

// This is used to cover up the shadow of the header that appears in the status bar.
const WhiteBoxToCoverHeaderShadow = () => {
  return (
    <Box
      height={60}
      style={{ marginTop: -60, zIndex: 1 }}
      bg="surface-primary"
    ></Box>
  );
};

const MoveBookingScreenQueryString = graphql`
  query MoveBookingScreenQuery($bookingId: ID!, $date: Date!) {
    session {
      ...TeeSheet_HeaderData
      currentClientProfile {
        member {
          membershipType {
            id
          }
        }
      }
    }
    node(id: $bookingId) {
      ... on Booking {
        __typename
        id

        startSlot {
          id
          agenda {
            date
            facility {
              type
            }
          }
        }
      }
    }
  }
`;
